import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { communityApi } from '../lib/api';
import {
  validateQuestion,
  validateAnswer,
  apiErrorMessage,
  ANSWER_MAX,
} from '../lib/communityValidation';
import { usePageSeo } from '../hooks/usePageSeo';
import VoteButton from '../components/community/VoteButton';
import ReportDialog from '../components/community/ReportDialog';
import QuestionFormFields from '../components/community/QuestionFormFields';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Calendar, User, MessageSquare, Trash2, LogIn, ShieldCheck,
  Pencil, Flag, CheckCircle2, Check,
} from 'lucide-react';
import { format } from 'date-fns';

const SITE_URL = 'https://autoxium.com';

// schema.org QAPage structured data -- lets search engines show the thread as a Q&A
// rich result. Injected client-side (Google renders JS); removed on unmount.
const useQaPageJsonLd = (question) => {
  useEffect(() => {
    if (!question) return undefined;
    const toAnswer = (a) => ({
      '@type': 'Answer',
      text: a.content,
      dateCreated: a.created_at,
      upvoteCount: a.vote_count,
      url: `${SITE_URL}/community/${question.question_id}#answer-${a.answer_id}`,
      author: { '@type': 'Person', name: a.author_name },
    });
    const accepted = question.answers.find((a) => a.answer_id === question.accepted_answer_id);
    const suggested = question.answers.filter((a) => a.answer_id !== question.accepted_answer_id);
    const data = {
      '@context': 'https://schema.org',
      '@type': 'QAPage',
      mainEntity: {
        '@type': 'Question',
        name: question.title,
        text: question.description,
        answerCount: question.answer_count,
        upvoteCount: question.vote_count,
        dateCreated: question.created_at,
        author: { '@type': 'Person', name: question.author_name },
        ...(accepted ? { acceptedAnswer: toAnswer(accepted) } : {}),
        ...(suggested.length ? { suggestedAnswer: suggested.map(toAnswer) } : {}),
      },
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(data);
    document.head.appendChild(script);
    return () => script.remove();
  }, [question]);
};

const QuestionDetailPage = () => {
  const { questionId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [question, setQuestion] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null); // 'not_found' | 'error'

  const [answerText, setAnswerText] = useState('');
  const [answerError, setAnswerError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [editQuestionOpen, setEditQuestionOpen] = useState(false);
  const [questionForm, setQuestionForm] = useState(null);
  const [questionFormErrors, setQuestionFormErrors] = useState({});
  const [savingQuestion, setSavingQuestion] = useState(false);

  const [editingAnswerId, setEditingAnswerId] = useState(null);
  const [editAnswerText, setEditAnswerText] = useState('');
  const [savingAnswer, setSavingAnswer] = useState(false);

  const [confirmDeleteQuestion, setConfirmDeleteQuestion] = useState(false);
  const [deleteAnswerId, setDeleteAnswerId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);

  const fetchQuestion = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setQuestion(await communityApi.getQuestion(questionId));
    } catch (err) {
      console.error('Error fetching question:', err);
      setLoadError(err.response?.status === 404 ? 'not_found' : 'error');
    } finally {
      setLoading(false);
    }
  }, [questionId]);

  useEffect(() => {
    fetchQuestion();
  }, [fetchQuestion, isAuthenticated]); // refetch on login/logout so viewer votes are right

  useEffect(() => {
    communityApi.getCategories().then(setCategories).catch(() => {});
  }, []);

  usePageSeo(
    question ? `${question.title} | AUTOXIUM Community` : null,
    question ? question.description.slice(0, 155) : null,
  );
  useQaPageJsonLd(question);

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'MMMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const isAdmin = user?.role === 'admin';
  const isMine = (authorId) => isAuthenticated && user?.user_id === authorId;
  const canModify = (authorId) => isMine(authorId) || (isAuthenticated && isAdmin);

  // ---------------------------------------------------------------- answers

  const handleAddAnswer = async (e) => {
    e.preventDefault();
    const error = validateAnswer(answerText);
    setAnswerError(error);
    if (error) return;
    setSubmitting(true);
    try {
      setQuestion(await communityApi.createAnswer(questionId, { content: answerText.trim() }));
      setAnswerText('');
      toast.success('Answer posted');
    } catch (err) {
      console.error('Error posting answer:', err);
      toast.error(apiErrorMessage(err, 'Failed to post answer'));
    } finally {
      setSubmitting(false);
    }
  };

  const startEditAnswer = (answer) => {
    setEditingAnswerId(answer.answer_id);
    setEditAnswerText(answer.content);
  };

  const handleSaveAnswer = async () => {
    const error = validateAnswer(editAnswerText);
    if (error) {
      toast.error(error);
      return;
    }
    setSavingAnswer(true);
    try {
      setQuestion(await communityApi.updateAnswer(editingAnswerId, editAnswerText.trim()));
      setEditingAnswerId(null);
      toast.success('Answer updated');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to update answer'));
    } finally {
      setSavingAnswer(false);
    }
  };

  const handleDeleteAnswer = async () => {
    setDeleting(true);
    try {
      setQuestion(await communityApi.deleteAnswer(deleteAnswerId));
      toast.success('Answer deleted');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to delete answer'));
    } finally {
      setDeleting(false);
      setDeleteAnswerId(null);
    }
  };

  const handleAccept = async (answerId) => {
    const clearing = question.accepted_answer_id === answerId;
    try {
      setQuestion(await communityApi.acceptAnswer(questionId, clearing ? null : answerId));
      toast.success(clearing ? 'Accepted answer cleared' : 'Answer accepted');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to update accepted answer'));
    }
  };

  // --------------------------------------------------------------- question

  const openEditQuestion = () => {
    setQuestionForm({ title: question.title, description: question.description, category: question.category });
    setQuestionFormErrors({});
    setEditQuestionOpen(true);
  };

  const handleSaveQuestion = async (e) => {
    e.preventDefault();
    const errors = validateQuestion(questionForm);
    setQuestionFormErrors(errors);
    if (Object.keys(errors).length) return;
    setSavingQuestion(true);
    try {
      setQuestion(await communityApi.updateQuestion(questionId, {
        title: questionForm.title.trim(),
        description: questionForm.description.trim(),
        category: questionForm.category,
      }));
      setEditQuestionOpen(false);
      toast.success('Question updated');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to update question'));
    } finally {
      setSavingQuestion(false);
    }
  };

  const handleDeleteQuestion = async () => {
    setDeleting(true);
    try {
      await communityApi.deleteQuestion(questionId);
      toast.success('Question deleted');
      navigate('/community');
    } catch (err) {
      console.error('Error deleting question:', err);
      toast.error(apiErrorMessage(err, 'Failed to delete question'));
      setDeleting(false);
      setConfirmDeleteQuestion(false);
    }
  };

  const updateQuestionVote = ({ voted, count }) =>
    setQuestion((q) => ({ ...q, viewer_has_voted: voted, vote_count: count }));

  const updateAnswerVote = (answerId) => ({ voted, count }) =>
    setQuestion((q) => ({
      ...q,
      answers: q.answers.map((a) =>
        a.answer_id === answerId ? { ...a, viewer_has_voted: voted, vote_count: count } : a),
    }));

  // ----------------------------------------------------------------- render

  if (loading && !question) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Skeleton className="h-8 w-32 mb-8" />
          <Skeleton className="h-12 w-3/4 mb-4" />
          <Skeleton className="h-6 w-full mb-8" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid="question-load-error">
        <div className="text-center max-w-md">
          <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h1 className="font-chivo text-2xl font-black uppercase mb-2">
            {loadError === 'not_found' ? 'Question Not Found' : "Couldn't Load Question"}
          </h1>
          <p className="text-muted-foreground mb-6">
            {loadError === 'not_found'
              ? 'It may have been deleted by its author or a moderator.'
              : 'Something went wrong on our side or your connection dropped.'}
          </p>
          <div className="flex justify-center gap-3 flex-wrap">
            {loadError === 'error' && <Button onClick={fetchQuestion}>Try Again</Button>}
            <Link to="/community"><Button variant="outline">Back to Community</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  if (!question) return null;

  const categoryLabel = categories.find((c) => c.key === question.category)?.label || question.category;
  const questionIsMine = isMine(question.author_id);
  const canAccept = canModify(question.author_id);

  return (
    <div className="min-h-screen bg-white" data-testid="question-detail-page">
      {/* Header */}
      <div className="bg-chassis text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link
            to="/community"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm mb-6"
            data-testid="back-to-community"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Community
          </Link>

          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Link to={`/community?category=${question.category}`}>
              <Badge variant="outline" className="border-white/30 text-white/80 hover:text-white font-mono text-[10px] uppercase tracking-wider">
                {categoryLabel}
              </Badge>
            </Link>
            {question.accepted_answer_id && (
              <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white border-0 font-mono text-[10px] uppercase tracking-wider">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Solved
              </Badge>
            )}
          </div>

          <h1 className="font-chivo text-2xl sm:text-3xl md:text-4xl font-black uppercase tracking-tight mb-4 break-words" data-testid="question-title">
            {question.title}
          </h1>
          <p className="text-white/70 text-base md:text-lg mb-4 whitespace-pre-wrap break-words" data-testid="question-description">
            {question.description}
          </p>
          <div className="flex items-center gap-x-6 gap-y-2 flex-wrap text-sm text-white/60 mb-5">
            <span className="flex items-center gap-2 min-w-0">
              <User className="h-4 w-4 shrink-0" />
              <span className="break-all">{question.author_name}</span>
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {formatDate(question.created_at)}
              {question.updated_at && <span className="italic">(edited)</span>}
            </span>
            <span className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              {question.answer_count} answer{question.answer_count !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="bg-white rounded-sm">
              <VoteButton
                targetType="question"
                targetId={question.question_id}
                count={question.vote_count}
                voted={question.viewer_has_voted}
                isOwn={questionIsMine}
                onChange={updateQuestionVote}
              />
            </div>
            {canModify(question.author_id) && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white"
                  onClick={openEditQuestion}
                  data-testid="edit-question-btn"
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white"
                  onClick={() => setConfirmDeleteQuestion(true)}
                  data-testid="delete-question-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </>
            )}
            {isAuthenticated && !questionIsMine && (
              <Button
                variant="ghost"
                size="sm"
                className="text-white/60 hover:bg-white/10 hover:text-white"
                onClick={() => setReportTarget({ type: 'question', id: question.question_id })}
                data-testid="report-question-btn"
              >
                <Flag className="h-4 w-4 mr-2" />
                Report
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Answers */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h2 className="font-chivo text-xl font-bold uppercase tracking-tight mb-6">
          {question.answer_count} Answer{question.answer_count !== 1 ? 's' : ''}
        </h2>

        {question.answers.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-structure rounded-sm mb-10">
            <MessageSquare className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No answers yet. Be the first to respond.</p>
          </div>
        ) : (
          <div className="space-y-6 mb-10">
            {question.answers.map((answer) => {
              const accepted = answer.answer_id === question.accepted_answer_id;
              const editing = editingAnswerId === answer.answer_id;
              return (
                <div
                  key={answer.answer_id}
                  id={`answer-${answer.answer_id}`}
                  className={`border rounded-sm p-4 sm:p-5 flex gap-3 sm:gap-4 ${
                    accepted ? 'border-emerald-600 border-2 bg-emerald-50/40' : 'border-structure'
                  }`}
                  data-testid={`answer-${answer.answer_id}`}
                >
                  <div className="flex flex-col items-center gap-2 shrink-0">
                    <VoteButton
                      targetType="answer"
                      targetId={answer.answer_id}
                      count={answer.vote_count}
                      voted={answer.viewer_has_voted}
                      isOwn={isMine(answer.author_id)}
                      onChange={updateAnswerVote(answer.answer_id)}
                    />
                    {canAccept ? (
                      <button
                        type="button"
                        onClick={() => handleAccept(answer.answer_id)}
                        title={accepted ? 'Unmark as accepted answer' : 'Mark as the accepted answer'}
                        aria-label={accepted ? 'Unmark as accepted answer' : 'Mark as the accepted answer'}
                        aria-pressed={accepted}
                        className={`flex items-center justify-center min-w-[44px] min-h-[44px] rounded-sm border transition-colors ${
                          accepted
                            ? 'border-emerald-600 bg-emerald-600 text-white'
                            : 'border-structure text-muted-foreground hover:border-emerald-600 hover:text-emerald-600'
                        }`}
                        data-testid={`accept-answer-${answer.answer_id}`}
                      >
                        <Check className="h-5 w-5" />
                      </button>
                    ) : accepted ? (
                      <CheckCircle2 className="h-6 w-6 text-emerald-600" aria-label="Accepted answer" />
                    ) : null}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-x-3 gap-y-1 mb-3 flex-wrap">
                      <span className="flex items-center gap-1 text-sm font-medium min-w-0">
                        <User className="h-3 w-3 shrink-0" />
                        <span className="break-all">{answer.author_name}</span>
                      </span>
                      {answer.is_admin_answer && (
                        <Badge
                          className="bg-autoxium-blue text-white border-0 font-mono text-[10px] uppercase tracking-wider"
                          data-testid={`admin-badge-${answer.answer_id}`}
                        >
                          <ShieldCheck className="h-3 w-3 mr-1" />
                          Admin
                        </Badge>
                      )}
                      {accepted && (
                        <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white border-0 font-mono text-[10px] uppercase tracking-wider">
                          Accepted
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground sm:ml-auto flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(answer.created_at)}
                        {answer.updated_at && <span className="italic">(edited)</span>}
                      </span>
                    </div>

                    {editing ? (
                      <div className="space-y-3">
                        <Textarea
                          value={editAnswerText}
                          onChange={(e) => setEditAnswerText(e.target.value)}
                          rows={5}
                          data-testid={`edit-answer-input-${answer.answer_id}`}
                        />
                        <div className="flex gap-2 flex-wrap">
                          <Button size="sm" onClick={handleSaveAnswer} disabled={savingAnswer}>
                            {savingAnswer ? 'Saving...' : 'Save'}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingAnswerId(null)}>Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-foreground leading-relaxed whitespace-pre-wrap break-words">{answer.content}</p>
                    )}

                    {!editing && isAuthenticated && (
                      <div className="flex items-center gap-1 mt-3 -ml-2 flex-wrap">
                        {canModify(answer.author_id) && (
                          <>
                            <Button variant="ghost" size="sm" className="h-8 text-muted-foreground"
                              onClick={() => startEditAnswer(answer)} data-testid={`edit-answer-${answer.answer_id}`}>
                              <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                            </Button>
                            <Button variant="ghost" size="sm" className="h-8 text-muted-foreground hover:text-destructive"
                              onClick={() => setDeleteAnswerId(answer.answer_id)} data-testid={`delete-answer-${answer.answer_id}`}>
                              <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                            </Button>
                          </>
                        )}
                        {!isMine(answer.author_id) && (
                          <Button variant="ghost" size="sm" className="h-8 text-muted-foreground"
                            onClick={() => setReportTarget({ type: 'answer', id: answer.answer_id })}
                            data-testid={`report-answer-${answer.answer_id}`}>
                            <Flag className="h-3.5 w-3.5 mr-1" /> Report
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Add Answer */}
        {isAuthenticated ? (
          <form onSubmit={handleAddAnswer} className="border-t border-structure pt-8" noValidate>
            <div className="flex items-center justify-between mb-4 gap-2">
              <h3 className="font-chivo text-lg font-bold uppercase tracking-tight">Add Your Answer</h3>
              <span className={`text-xs font-mono ${answerText.trim().length > ANSWER_MAX ? 'text-destructive' : 'text-muted-foreground'}`}>
                {answerText.trim().length}/{ANSWER_MAX}
              </span>
            </div>
            <Textarea
              value={answerText}
              onChange={(e) => { setAnswerText(e.target.value); if (answerError) setAnswerError(null); }}
              placeholder="Share your knowledge..."
              rows={5}
              className="mb-2"
              aria-invalid={!!answerError}
              data-testid="answer-input"
            />
            {answerError && <p className="text-xs text-destructive mb-2">{answerError}</p>}
            <Button
              type="submit"
              disabled={submitting}
              className="mt-2 bg-autoxium-blue hover:bg-autoxium-blue/90 text-white font-bold uppercase tracking-wider"
              data-testid="submit-answer-btn"
            >
              {submitting ? 'Posting...' : 'Post Answer'}
            </Button>
          </form>
        ) : (
          <div className="border-t border-structure pt-8 text-center">
            <p className="text-muted-foreground mb-4">Login to add your answer.</p>
            <Link to="/login" state={{ from: `/community/${questionId}` }}>
              <Button variant="outline" className="font-bold uppercase tracking-wider" data-testid="login-to-answer-btn">
                <LogIn className="h-4 w-4 mr-2" />
                Login
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Edit Question */}
      <Dialog open={editQuestionOpen} onOpenChange={setEditQuestionOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="edit-question-dialog">
          <DialogHeader>
            <DialogTitle className="font-chivo uppercase">Edit Question</DialogTitle>
          </DialogHeader>
          {questionForm && (
            <form onSubmit={handleSaveQuestion} className="space-y-4" noValidate>
              <QuestionFormFields
                value={questionForm}
                errors={questionFormErrors}
                categories={categories}
                onChange={setQuestionForm}
                idPrefix="edit-question"
              />
              <DialogFooter>
                <Button
                  type="submit"
                  disabled={savingQuestion}
                  className="bg-autoxium-blue hover:bg-autoxium-blue/90 text-white font-bold uppercase tracking-wider"
                  data-testid="save-question-btn"
                >
                  {savingQuestion ? 'Saving...' : 'Save Changes'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Question Confirmation */}
      <AlertDialog open={confirmDeleteQuestion} onOpenChange={setConfirmDeleteQuestion}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-chivo uppercase">Delete Question?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete this question and all its answers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteQuestion}
              disabled={deleting}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-question-btn"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Answer Confirmation */}
      <AlertDialog open={!!deleteAnswerId} onOpenChange={(open) => { if (!open) setDeleteAnswerId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-chivo uppercase">Delete Answer?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAnswer}
              disabled={deleting}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-answer-btn"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ReportDialog target={reportTarget} onClose={() => setReportTarget(null)} />
    </div>
  );
};

export default QuestionDetailPage;
